import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { WinStand } from './WinPunktetafel';

/** Eigene Verwaltung für Punkte und Ergebnisse der Challenge-Umfragen. */
export function WinChallengeVerwaltung({ lobbyId }: { lobbyId: string }) {
  const [stand, setStand] = useState<WinStand | null>(null);
  const [challengeRuns, setChallengeRuns] = useState<{id:string;state:string;openedAt:string}[]>([]);
  const [challengePunkteEingabe, setChallengePunkteEingabe] = useState('0');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const laden = useCallback(async () => {
    const [neu, ergebnisse] = await Promise.all([api.winMatches(lobbyId), api.winChallengeResults(lobbyId)]);
    setStand(neu);
    setChallengeRuns(ergebnisse.runs);
  }, [lobbyId]);
  useEffect(() => { void laden().catch(err => setFehler(String(err))); }, [laden]);
  useEffect(() => { if (stand) setChallengePunkteEingabe(String(stand.challengePoints)); }, [stand?.challengePoints]);
  async function tun(fn: () => Promise<unknown>) {
    setBusy(true); setFehler('');
    try { await fn(); await laden(); }
    catch (err) { setFehler(err instanceof Error ? err.message : 'Aktion fehlgeschlagen.'); }
    finally { setBusy(false); }
  }
  return <section className="panel">
    <h2>Challenge-Ergebnisse &amp; Extrapunkte</h2>
    <h3>Challenge-Extrapunkte</h3>
    <p className="muted small">Jede bestandene Challenge zählt pro Team und Umfragedurchlauf höchstens einmal. Änderungen ersetzen den vorherigen Status.</p>
    <label><input type="checkbox" checked={stand?.challengePointsEnabled ?? false} onChange={event => void tun(() => api.winChallengeSettings(lobbyId, event.target.checked, stand?.challengePoints ?? 0))} /> Extrapunkte für bestandene Challenges</label>
    <form onSubmit={event => {
      event.preventDefault();
      const points = Number(challengePunkteEingabe);
      if (!Number.isInteger(points) || points < 0 || points > 100000) {
        setFehler('Bitte eine ganze Punktzahl zwischen 0 und 100000 eingeben.');
        return;
      }
      void tun(() => api.winChallengeSettings(lobbyId, stand?.challengePointsEnabled ?? false, points));
    }}>
      <label>Punkte pro Erfolg
        <input type="number" min={0} max={100000} step={1} value={challengePunkteEingabe}
          onChange={event => setChallengePunkteEingabe(event.target.value)} />
      </label>
      <button type="submit" disabled={busy || !stand}>Punkte-Einstellung speichern</button>
    </form>
    <h3>Challenge-Ergebnisse</h3>
    {challengeRuns.filter(run => run.state === 'CLOSED').slice(-15).reverse().map(run => <div key={run.id} className="panel">
      <strong>Umfrage vom {new Date(run.openedAt).toLocaleString('de-DE')}</strong>
      <div className="row">{stand?.teams.map(team => {
        const result = stand.results.find(r => r.runId === run.id && r.teamId === team.id);
        return <div key={team.id}><span>{team.name}: </span>
          <select disabled={busy} value={result ? (result.passed ? 'yes' : 'no') : ''} onChange={event => {
            if (!event.target.value) return;
            void tun(() => api.winChallengeResult(lobbyId, run.id, team.id, event.target.value === 'yes'));
          }}><option value="">Nicht bewertet</option><option value="yes">Bestanden</option><option value="no">Nicht bestanden</option></select>
        </div>;
      })}</div>
    </div>)}
    {fehler && <p role="alert" className="notice">{fehler}</p>}
  </section>;
}
